import { money } from "@stb/core/money"
import { calculateDocumentGst, calculateLineGst } from "@stb/jurisdiction-au"

import { localizeHref } from "@/core/i18n/locale-context"

import { GstCalculator, SimpleTable, SourceLine, formatAud } from "../../components/landing"
import { gstGuide } from "../gst-calculator"
import type { Guide } from "../guide-types"
import { englishDateToZh } from "./dates"

/**
 * Chinese counterpart of content/gst-calculator.tsx (T1320). Same sources, same dates, same engine; the only
 * differences are the words. Kestly is not registered for GST: never write GST wording about Kestly's own prices.
 * Terms: docs/design/landing/copy-zh.md.
 */
const [howGstWorks, registering, taxInvoices] = gstGuide.sources
const ATO_HOW_GST_WORKS = howGstWorks.href
const ATO_REGISTER = registering!.href
const ATO_TAX_INVOICES = taxInvoices!.href
const ATO_GST =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst"
const checked = englishDateToZh(gstGuide.checked)
const fy = gstGuide.financialYear

/** The same three examples as the English page, from the same engine the calculator uses. */
const example = calculateLineGst({
  amount: money(11000, "AUD"),
  taxCode: "GST",
  amountsAre: "inclusive",
})
const addExample = calculateLineGst({
  amount: money(25000, "AUD"),
  taxCode: "GST",
  amountsAre: "exclusive",
})
const removeExample = calculateLineGst({
  amount: money(9995, "AUD"),
  taxCode: "GST",
  amountsAre: "inclusive",
})

const EXAMPLE_ITEMS: [string, number][] = [
  ["刹车片", 11000],
  ["机油和机滤", 5555],
  ["雨刷片", 1999],
]
const itemsExample = calculateDocumentGst(
  EXAMPLE_ITEMS.map(([, cents]) => ({ amount: money(cents, "AUD"), taxCode: "GST" as const })),
  { amountsAre: "inclusive", rounding: "per-line" },
)

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const h2 = "text-2xl font-semibold"
const para = "max-w-[70ch]"
const list = "list-disc space-y-1 pl-6"

const SRC_HOW = { label: "ATO，GST 如何运作", href: ATO_HOW_GST_WORKS }
const SRC_REGISTER = { label: "ATO，登记 GST", href: ATO_REGISTER }
const SRC_INVOICES = { label: "ATO，Tax Invoice", href: ATO_TAX_INVOICES }

export const gstGuideZh: Guide = {
  slug: gstGuide.slug,
  title: "澳洲 GST 计算器：加或拆 GST",
  description:
    "免费的澳大利亚 GST 计算器：给价格加 10% GST，或从含 GST 的总额里拆出 GST（除以 11）。附四舍五入规则、A$75,000 登记门槛和 ATO 来源。",
  crumb: "GST 计算器",
  h1: "澳洲 GST 计算器：加 GST 或拆出 GST",
  published: gstGuide.published,
  reviewed: gstGuide.reviewed,
  badge: "免费工具",
  financialYear: fy,
  checked,
  sources: [SRC_HOW, SRC_REGISTER, SRC_INVOICES],
  answer: (
    <>
      澳大利亚的 GST（商品及服务税）是不含 GST 价格的 10%。要加 GST，把不含 GST 的价格乘以
      1.1。要从已含 GST 的价格里算出 GST，把价格除以 11，再用总额减去这个结果，就是不含 GST
      的价格。例如，含 GST 的 {formatAud(example.gross)} 就是 {formatAud(example.net)} 加{" "}
      {formatAud(example.gst)} GST。
    </>
  ),
  related: [
    { label: "Kestly 开发票：发 Tax Invoice，看谁已付款", href: "/invoicing" },
    { label: "免费发票生成器：生成 PDF 的 Tax Invoice", href: "/invoice-generator" },
    {
      label: "Tax Invoice 要求：每张发票必须写什么",
      href: "/guides/tax-invoice-requirements",
    },
    { label: "工资计算器：税后到手工资", href: "/pay-calculator" },
    {
      label: "Cents per km 计算器：本年度 ATO 车辆费率",
      href: "/cents-per-km-calculator",
    },
    { label: "全部指南和免费工具", href: "/guides" },
  ],
  cta: {
    title: "Kestly 帮你在 Tax Invoice 上算好 GST",
    description: "生成会自动加好 GST 的 Tax Invoice。免费，不用注册账号。",
    ctaLabel: "打开发票生成器",
    ctaHref: "/invoice-generator?from=gst-calculator",
  },
  toc: [
    { id: "calculator", label: "计算 GST" },
    { id: "formulas", label: "GST 公式" },
    { id: "add-gst", label: "加 GST" },
    { id: "remove-gst", label: "拆出 GST" },
    { id: "inclusive-exclusive", label: "含 GST 与不含 GST" },
    { id: "several-items", label: "四舍五入" },
    { id: "what-is-gst", label: "什么是 GST" },
    { id: "threshold", label: "登记门槛" },
    { id: "tax-invoices", label: "Tax Invoice" },
    { id: "faq", label: "常见问题" },
  ],
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className={h2}>
          计算 GST
        </h2>
        <p className={para}>价格还没含 GST，选“加 GST”；总额已经含 GST，选“拆出 GST”。</p>
        <GstCalculator />
      </section>

      <section aria-labelledby="formulas" className="grid gap-3">
        <h2 id="formulas" className={h2}>
          GST 怎么算：公式
        </h2>
        <p className={para}>
          三个算式能解决大多数 GST 问题：乘以 1.1 是加 GST，除以 11 是算出总额里的 GST，除以 1.1
          是得到不含 GST 的价格。
        </p>
        <SimpleTable
          caption="GST 公式，以 A$100 和 A$110 为例"
          columns={["目的", "怎么算", "示例"]}
          rows={[
            {
              header: "给价格加 GST",
              cells: ["乘以 1.1", `${formatAud(example.net)} 变成 ${formatAud(example.gross)}`],
            },
            {
              header: "算出总额里的 GST",
              cells: ["除以 11", `${formatAud(example.gross)} 里含 ${formatAud(example.gst)}`],
            },
            {
              header: "从总额里拆出 GST",
              cells: ["除以 1.1", `${formatAud(example.gross)} 变成 ${formatAud(example.net)}`],
            },
          ]}
        />
        <SourceLine sources={[SRC_HOW]} financialYear={fy} checked={checked} />
      </section>

      <section aria-labelledby="add-gst" className="grid gap-3">
        <h2 id="add-gst" className={h2}>
          给价格加 GST
        </h2>
        <p className={para}>
          把不含 GST 的价格乘以 1.1。一份报价是 {formatAud(addExample.net)} 另加 GST，总共就是{" "}
          {formatAud(addExample.gross)}：{formatAud(addExample.net)} 是工钱，
          {formatAud(addExample.gst)} 是 GST。想单独看 GST，改成乘以 0.1。
        </p>
      </section>

      <section aria-labelledby="remove-gst" className="grid gap-3">
        <h2 id="remove-gst" className={h2}>
          从价格里拆出 GST（反推 GST）
        </h2>
        <p className={para}>
          把含 GST 的总额除以 11，得到 GST，再用总额减去它。一张 {formatAud(removeExample.gross)}{" "}
          的收据含 {formatAud(removeExample.gst)} 的 GST，所以不含 GST 的价格是{" "}
          {formatAud(removeExample.net)}。
        </p>
        <p className={para}>
          为什么除以 11，不是 10？GST 是不含 GST 价格的 10%。加上 GST 后，总额是 11 份，其中一份是
          GST。直接从总额里减去 10% 会算错：A$110 的 10% 是 A$11，但里面的 GST 是 A$10。
        </p>
      </section>

      <section aria-labelledby="inclusive-exclusive" className="grid gap-3">
        <h2 id="inclusive-exclusive" className={h2}>
          含 GST 价格与不含 GST 价格
        </h2>
        <SimpleTable
          caption="同一笔销售的两种写法"
          columns={["价格是", "常见写法", "意思", "示例"]}
          rows={[
            {
              header: "含 GST（GST-inclusive）",
              cells: [
                "incl. GST",
                "价格里已经包含 GST",
                `${formatAud(example.gross)}，其中 ${formatAud(example.gst)} 是 GST`,
              ],
            },
            {
              header: "不含 GST（GST-exclusive）",
              cells: [
                "ex GST、excl. GST、plus GST",
                "GST 还要另外加",
                `${formatAud(example.net)}，另加 ${formatAud(example.gst)} GST`,
              ],
            },
          ]}
        />
        <p className={para}>
          金额含 GST，在计算器里选“拆出 GST”；金额不含 GST，选“加 GST”。ATO 说明，已登记 GST
          的企业会把 GST 包含在报价里。
        </p>
        <SourceLine sources={[SRC_HOW]} financialYear={fy} checked={checked} />
      </section>

      <section aria-labelledby="several-items" className="grid gap-3">
        <h2 id="several-items" className={h2}>
          GST 怎么四舍五入
        </h2>
        <p className={para}>
          单笔销售，GST 四舍五入到分；半分进位。一张发票有多个项目时，ATO
          介绍了两种方法：按发票总额算 GST 并只取整一次，或者每个项目分别算
          GST，加起来再对总额取整。两种方法的总额可能差一点点，你和客户也不必用同一种。
        </p>
        <p className={para}>
          这个计算器用按项目的方法。勾选“多个项目”，最多输入 20 行：每行的 GST
          先四舍五入到分，再把各行加起来。我们的发票生成器则是按发票总额只取整一次，所以行数多的发票，结果可能和这个工具差一两分。
        </p>
        <SimpleTable
          caption="三个含 GST 的项目，每行单独取整"
          columns={["项目", "不含 GST", "GST", "含 GST"]}
          rows={[
            ...EXAMPLE_ITEMS.map(([name], i) => {
              const l = itemsExample.lines[i]!
              return {
                header: name,
                cells: [formatAud(l.net), formatAud(l.gst), formatAud(l.gross)],
              }
            }),
            {
              header: "合计",
              cells: [
                formatAud(itemsExample.totals.net),
                formatAud(itemsExample.totals.gst),
                formatAud(itemsExample.totals.gross),
              ],
            },
          ]}
        />
        <SourceLine
          sources={[{ label: "ATO，Tax Invoice（取整）", href: ATO_TAX_INVOICES }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="what-is-gst" className="grid gap-3">
        <h2 id="what-is-gst" className={h2}>
          什么是 GST
        </h2>
        <p className={para}>
          GST（商品及服务税）是对在澳大利亚销售或消费的大多数商品、服务及其他项目征收的 10%
          广泛税种。已登记 GST 的企业把 GST 包含在售价里，并可以为业务采购中含的 GST
          申请抵免。GST-free（免 GST）或 input-taxed（进项税免税）的销售没有 GST
          可加，所以这个计算器不适用于它们。
        </p>
        <SourceLine
          sources={[SRC_HOW, { label: "ATO，GST", href: ATO_GST }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="threshold" className="grid gap-3">
        <h2 id="threshold" className={h2}>
          A$75,000 的登记门槛
        </h2>
        <p className={para}>
          你的 GST turnover（GST 营业额）达到 A$75,000 或以上时，必须登记 GST。GST
          营业额是你的总收入减去 GST。以下情况也必须登记：
        </p>
        <ul className={`${para} ${list}`}>
          <li>你是新企业，预计第一年就会达到 A$75,000</li>
          <li>你经营非营利机构，GST 营业额达到 A$150,000 或以上</li>
          <li>你提供出租车、豪华轿车或网约车（ATO 称 ride-sourcing）服务，不论营业额多少</li>
          <li>你想申请燃油税抵免。</li>
        </ul>
        <p className={para}>
          其他情况下，是否登记由你选择；一旦登记，通常要保持登记 12 个月。超过门槛后 21
          天内要完成登记。登记 GST 之前要先有 ABN，登记之后在 BAS 上申报 GST。
        </p>
        <SourceLine sources={[SRC_REGISTER]} financialYear={fy} checked={checked} />
      </section>

      <section aria-labelledby="tax-invoices" className="grid gap-3">
        <h2 id="tax-invoices" className={h2}>
          什么时候必须给 Tax Invoice
        </h2>
        <p className={para}>
          如果你已登记 GST，客户要求开 Tax Invoice，你必须在 28 天内给他们。含 GST 总额在 A$82.50
          或以下的销售不必开。PDF 或其他电子发票都可以。金额低于 A$1,000 的销售，发票要写明：
        </p>
        <ul className={`${para} ${list}`}>
          <li>这份文件是 Tax Invoice</li>
          <li>你的身份和 ABN</li>
          <li>开票日期</li>
          <li>所售物品的简要说明，包括数量和价格</li>
          <li>
            GST 金额，单独一行写出；如果 GST 正好是总额的十一分之一，也可以写“Total price includes
            GST”这类说明
          </li>
          <li>每笔销售中应税的部分有多少。</li>
        </ul>
        <p className={para}>
          销售额达到 A$1,000 或以上时，发票还要写买方的身份或 ABN。我们的{" "}
          <a href="/guides/tax-invoice-requirements" className={linkClass}>
            Tax Invoice 要求指南
          </a>{" "}
          逐项讲解，
          <a href={localizeHref("/invoice-generator", "zh")} className={linkClass}>
            免费发票生成器
          </a>{" "}
          能生成算好 GST 的 PDF Tax Invoice。
        </p>
        <SourceLine sources={[SRC_INVOICES]} financialYear={fy} checked={checked} />
      </section>
    </>
  ),
  faq: [
    {
      question: "怎么给价格算 GST？",
      answer:
        "把不含 GST 的价格乘以 0.1 得到 GST，乘以 1.1 得到含 GST 的价格。A$100 的 GST 是 A$10，总额是 A$110。",
    },
    {
      question: "怎么从总额里拆出 GST？",
      answer:
        "把含 GST 的总额除以 11 得到 GST，再用总额减去它。A$110 里含 A$10 的 GST，所以不含 GST 的价格是 A$100。把总额除以 1.1，得到的同样是 A$100。",
    },
    {
      question: "为什么除以 11，不是 10？",
      answer:
        "GST 是不含 GST 价格的 10%，所以含 GST 的总额是 11 份，GST 是其中一份。从 A$110 里减去 10% 得到 A$11，但里面的 GST 是 A$10。",
    },
    {
      question: "NSW、昆士兰和 WA 的 GST 一样吗？",
      answer: `一样。ATO 把 GST 描述为对在澳大利亚销售或消费的大多数商品和服务征收的 10% 广泛税种，所以每个州和领地都是同样的 10%。来源：ATO，GST 如何运作，核对于 ${checked}。`,
    },
    {
      question: "含 GST 和不含 GST 有什么区别？",
      answer:
        "含 GST 的价格已经包含 GST：含 GST 的 A$110 是 A$100 加 A$10 GST。不含 GST（ex GST）的价格还没加 GST：A$100 ex GST，加上 GST 后是 A$110。",
    },
    {
      question: "我需要登记 GST 吗？",
      answer: `你的 GST 营业额（总收入减去 GST）达到 A$75,000 时必须登记，非营利机构是 A$150,000。出租车、豪华轿车和网约车司机不论营业额多少都必须登记。低于门槛时，一般由你选择。来源：ATO，登记 GST，核对于 ${checked}。`,
    },
    {
      question: "什么时候必须给 Tax Invoice？",
      answer: `如果你已登记 GST，客户要求开 Tax Invoice，你必须在 28 天内给他们。含 GST 总额在 A$82.50 或以下的销售不必开。来源：ATO，Tax Invoice，核对于 ${checked}。`,
    },
    {
      question: "GST 怎么四舍五入？",
      answer: `四舍五入到分，半分进位。多个项目时，ATO 允许按发票总额只取整一次，或者每个项目分别算 GST 再相加。这个计算器每行取整到分，再把各行相加。来源：ATO，Tax Invoice，核对于 ${checked}。`,
    },
    {
      question: "这是 ATO 的 GST 计算器吗？",
      answer: `不是。这个 GST 计算器由 Kestly 独立开发，不是 ATO 的工具。它使用这里链接的 ATO 页面所述的 10% 税率和取整规则，最近核对于 ${checked}。`,
    },
  ],
}
